/**
 * @file RepTopCustomersWidget.js
 * @version 2.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-03
 * @modified 2026-10-08
 *
 * A rep's biggest accounts by tonnage. Answers "who is carrying my number".
 *
 * Three controls, all cheap:
 *   - year / month, because "my best customers" means different things over a
 *     season and over a year
 *   - swipe (or the chevrons) to page back through periods
 *   - a foot button that grows the list five at a time
 *
 * The list opens at five deliberately. A rep glances at the top of it far more
 * often than they read it, and a widget that opens at full length pushes
 * everything below it off a phone screen.
 *
 * Rows open the shared ContactModal by account id — the same click-through the
 * data-quality widget uses, so a rep goes from a name to the record without
 * navigating.
 */

import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import ContactModal from '../components/ContactModal';
import useRepDashboard, { formatTonnes, formatCompactMoney } from './useRepDashboard';
import useSwipe from '../hooks/useSwipe';
import RepRefreshButton from './RepRefreshButton';
import './RepWidgets.css';

const STEP = 5;

function RepTopCustomersWidget({ variant = 'desktop' }) {
  const [period, setPeriod] = useState('year');
  const [offset, setOffset] = useState(0);
  const [shown, setShown] = useState(STEP);
  const [openAccountId, setOpenAccountId] = useState(null);

  // Ask for one more than we show, so "more to come" is known without a second
  // request — and cap it so paging back never pulls a thousand rows.
  const { data, loading, message, refresh } = useRepDashboard('top-customers', {
    params: { period, offset, limit: Math.min(shown + 1, 100) }
  });

  const rows = data?.customers || [];
  const visible = rows.slice(0, shown);
  const total = data?.totalWithTonnes ?? rows.length;
  const hasMore = total > visible.length;

  // Bar widths are relative to the biggest account IN VIEW, so the shape of the
  // patch reads at a glance; an absolute scale would be meaningless across reps.
  const max = visible.reduce((m, r) => Math.max(m, r.tonnes), 0) || 1;

  const swipeRef = useRef(null);
  const changePeriod = (delta) => {
    // Forward is "more recent", so it decreases the offset. Never past today.
    setOffset(o => Math.max(0, Math.min(20, o + delta)));
    setShown(STEP);
  };
  const { dx } = useSwipe(swipeRef, {
    onLeft: () => changePeriod(1),    // drag left = go back in time
    onRight: () => changePeriod(-1),
    enabled: variant === 'phone'
  });

  const setMode = (next) => {
    setPeriod(next);
    setOffset(0);
    setShown(STEP);
  };

  return (
    <div className="dashboard-card rep-widget rep-top-customers-card">
      <div className="card-header">
        <h3>
          <span className="material-symbols-rounded">leaderboard</span>
          Top Customers
        </h3>
        <div className="header-controls">
          <RepRefreshButton cachedAt={data?.cachedAt} loading={loading} onRefresh={refresh} />
        </div>
      </div>

      <div className="card-body">
        <div className="rep-tc-controls">
          <div className="rep-tc-period">
            <button
              type="button" className="btn-icon"
              onClick={() => changePeriod(1)}
              disabled={offset >= 20}
              aria-label={`Previous ${period}`}
            >
              <span className="material-symbols-rounded">chevron_left</span>
            </button>
            <strong>{data?.label || '—'}</strong>
            <button
              type="button" className="btn-icon"
              onClick={() => changePeriod(-1)}
              disabled={offset === 0}
              aria-label={`Next ${period}`}
            >
              <span className="material-symbols-rounded">chevron_right</span>
            </button>
          </div>

          <div className="rep-tc-mode" role="group" aria-label="Period">
            <button
              type="button"
              className={period === 'year' ? 'active' : ''}
              onClick={() => setMode('year')}
            >Year</button>
            <button
              type="button"
              className={period === 'month' ? 'active' : ''}
              onClick={() => setMode('month')}
            >Month</button>
          </div>
        </div>

        {loading && <div className="rep-widget-state">Loading…</div>}
        {!loading && message && <div className="rep-widget-state">{message}</div>}
        {!loading && !message && visible.length === 0 && (
          <div className="rep-widget-state">Nothing invoiced in {data?.label || 'this period'}.</div>
        )}

        {!loading && !message && visible.length > 0 && (
          <div
            className="rep-tc-swipe"
            ref={swipeRef}
            style={{ transform: dx ? `translateX(${dx * 0.4}px)` : undefined }}
          >
            <ol className="rep-rank-list">
              {visible.map((r, i) => (
                <li key={r.accountId}>
                  <button
                    type="button"
                    className="rep-rank-row"
                    onClick={() => setOpenAccountId(r.accountId)}
                    title={`Open ${r.companyName}`}
                  >
                    <span className="rep-rank-pos">{i + 1}</span>
                    <span className="rep-rank-body">
                      <span className="rep-rank-name">{r.companyName}</span>
                      <span
                        className="rep-rank-bar"
                        style={{ '--rep-bar': `${Math.round((r.tonnes / max) * 100)}%` }}
                      />
                    </span>
                    <span className="rep-rank-figures">
                      <span className="rep-rank-tonnes">{formatTonnes(r.tonnes)}</span>
                      <span className="rep-rank-value">{formatCompactMoney(r.value)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>

            {hasMore && (
              <button
                type="button"
                className="rep-show-more"
                onClick={() => setShown(n => n + STEP)}
              >
                <span className="material-symbols-rounded">expand_more</span>
                Show {Math.min(STEP, total - visible.length)} more
                <span className="rep-show-more-count">({visible.length} of {total})</span>
              </button>
            )}
          </div>
        )}
      </div>

      {openAccountId && ReactDOM.createPortal(
        <ContactModal contactId={openAccountId} onClose={() => setOpenAccountId(null)} />,
        document.body
      )}
    </div>
  );
}

export default RepTopCustomersWidget;
